// Cuadro previo a la baja de un médico en el panel de Usuarios del HCE (PLAN Sala C11.3): cuántas citas por atender deja y a quién pasarlas o
// dejarlas sin médico. Con citas por atender no se puede continuar sin elegir una de las dos cosas (el servidor lo exige también).
// Textos y reglas en lib/bajaProfesional.ts (lógica pura, probada aparte).
import type { JSX } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ELIGE_SIN_MEDICO, TEXTOS_BAJA, errorDeBaja, lineasDeBaja, type ResumenPreviaBaja } from '@/lib/bajaProfesional';

type Props = {
  nombre: string;
  /** `null` mientras se lee del servidor. */
  resumen: ResumenPreviaBaja | null;
  enviando: boolean;
  error: string | null;
  onConfirmar: (eleccion: string) => void;
  onCancelar: () => void;
};

export function BajaMedicoPanel({ nombre, resumen, enviando, error, onConfirmar, onCancelar }: Props): JSX.Element {
  const [eleccion, setEleccion] = useState('');
  const [errorLocal, setErrorLocal] = useState<string | null>(null);

  if (!resumen) {
    return (
      <div className="flex items-center gap-2">
        <p role="status" className="text-xs text-hce-clay">
          {TEXTOS_BAJA.cargando}
        </p>
        <Button variant="outline" size="sm" onClick={onCancelar}>
          {TEXTOS_BAJA.cancelar}
        </Button>
      </div>
    );
  }

  // Solo se pregunta a quién pasar las citas cuando HAY citas por atender; sin ellas no hay nada que decidir.
  const hayQueElegir = resumen.requiereDecision;
  const mensaje = errorLocal ?? error;
  const grupo = `reemplazo-${nombre}`;

  function confirmar(): void {
    if (!resumen) return;
    const problema = errorDeBaja(resumen, eleccion);
    setErrorLocal(problema);
    if (!problema) onConfirmar(eleccion);
  }

  return (
    <div role="group" aria-label={`Dar de baja a ${nombre}`} data-panel-baja="" className="space-y-2 rounded-md border border-hce-clay/40 bg-hce-clay/5 p-3">
      <p className="text-sm font-semibold">Dar de baja a {nombre}</p>
      {lineasDeBaja(resumen).map((l) => (
        <p key={l} className="text-xs">
          {l}
        </p>
      ))}
      {hayQueElegir && (
        <fieldset className="space-y-1.5" disabled={enviando}>
          <legend className="text-xs font-medium">{TEXTOS_BAJA.pregunta}</legend>
          {resumen.reemplazos.length === 0 && <p className="text-xs text-hce-clay">{TEXTOS_BAJA.sinOtroMedico}</p>}
          {resumen.reemplazos.map((r) => (
            <label key={r.practitionerId} className="flex items-center gap-2 text-xs">
              <input type="radio" name={grupo} value={r.practitionerId} checked={eleccion === r.practitionerId} onChange={() => setEleccion(r.practitionerId)} />
              Pasarlas a {r.nombre}
            </label>
          ))}
          <label className="flex items-center gap-2 text-xs">
            <input type="radio" name={grupo} value={ELIGE_SIN_MEDICO} checked={eleccion === ELIGE_SIN_MEDICO} onChange={() => setEleccion(ELIGE_SIN_MEDICO)} />
            {TEXTOS_BAJA.sinMedico}
          </label>
        </fieldset>
      )}
      {mensaje && (
        <p role="alert" className="text-xs text-hce-clay">
          {mensaje}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button variant="destructive" size="sm" disabled={enviando} onClick={confirmar}>
          {enviando ? 'Dando de baja…' : TEXTOS_BAJA.confirmar}
        </Button>
        <Button variant="outline" size="sm" disabled={enviando} onClick={onCancelar}>
          {TEXTOS_BAJA.cancelar}
        </Button>
      </div>
    </div>
  );
}
